import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { Container, PageHeader, Spinner } from "@/components/shared";
import { FicheContent, ShareBar } from "@/components/RightsFiche";
import { toast } from "sonner";
import {
  Briefcase, Home, GraduationCap, Store, Landmark, IdCard, HelpCircle,
  ArrowLeft, Sparkles, ChevronRight,
} from "lucide-react";

const ICONS = { Briefcase, Home, GraduationCap, Store, Landmark, IdCard, HelpCircle };

export default function RightsHelp() {
  const navigate = useNavigate();
  const [situations, setSituations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [sheets, setSheets] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/rights-situations").then(({ data }) => setSituations(data.situations || [])).catch((e) => toast.error(apiErr(e)));
  }, []);

  const choose = async (situ) => {
    setSelected(situ);
    if (situ.key === "autre" || !situ.slugs?.length) { setSheets([]); return; }
    setLoading(true); setSheets(null);
    try {
      const results = await Promise.all(situ.slugs.map((slug) => api.get(`/rights/${slug}`).then((r) => r.data)));
      setSheets(results);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };

  return (
    <Container>
      <Link to="/rights" className="text-white/50 hover:text-gold text-sm inline-flex items-center gap-2 mb-6"><ArrowLeft size={16} /> Vos droits</Link>
      <PageHeader eyebrow="Aide" title="Je pense être victime d'une discrimination"
        subtitle="Choisissez la situation qui vous concerne. Nous affichons les lois, vos droits, les sanctions, les preuves, les démarches, les délais et les organismes à contacter." />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-8">
        {situations.map((s) => {
          const Icon = ICONS[s.icon] || HelpCircle;
          const active = selected?.key === s.key;
          return (
            <button key={s.key} data-testid={`situation-${s.key}`} onClick={() => choose(s)}
              className={`card-surface p-4 text-left transition-colors ${active ? "border-gold/50 bg-gold/[0.06]" : "hover:border-gold/30"}`}>
              <Icon size={22} className={active ? "text-gold" : "text-white/70"} strokeWidth={1.5} />
              <p className="text-sm mt-2 font-medium">{s.label}</p>
            </button>
          );
        })}
      </div>

      {loading && <Spinner label="Chargement des informations..." />}

      {selected?.key === "autre" && sheets && (
        <div className="card-surface p-6 text-center" data-testid="situation-autre-cta">
          <Sparkles size={22} className="text-gold mx-auto mb-2" />
          <h3 className="font-display text-lg mb-2">Décrivez votre situation à l'assistant</h3>
          <p className="text-white/55 text-sm max-w-md mx-auto mb-4">Notre assistant IA identifie les règles applicables, vos droits et les démarches possibles.</p>
          <button onClick={() => navigate("/rights")} className="inline-flex items-center gap-2 gold-gradient text-black rounded-full px-6 py-2.5 font-medium">
            Ouvrir l'assistant <ChevronRight size={16} />
          </button>
        </div>
      )}

      {sheets && sheets.length > 0 && (
        <div className="space-y-10" data-testid="situation-results">
          {sheets.map((sheet) => (
            <div key={sheet.slug}>
              <div className="flex items-center justify-between gap-3 mb-4">
                <h2 className="font-display text-2xl tracking-tight">{sheet.title}</h2>
                <Link to={`/rights/${sheet.slug}`} className="text-gold text-sm hover:underline shrink-0">Fiche complète →</Link>
              </div>
              <div className="mb-4"><ShareBar slug={sheet.slug} title={sheet.title} summary={sheet.summary} /></div>
              <FicheContent sheet={sheet} />
            </div>
          ))}
        </div>
      )}
    </Container>
  );
}
